/**
 * DynamicPriceDrop - styled components
 * Shell = Container (100% width); Inner = ContentInner (max 1440 centered)
 */

import styled from 'styled-components'
import { GREEN_STATUS } from '../../theme/colors'
import { ContentInner } from '../../theme/components'

export const Container = styled.div<{ order?: number }>`
  box-sizing: border-box;
  width: 100%;
  border-top: var(--border-regular) solid var(--neutral-border-light);
  background: var(--neutral-white);
  order: ${({ order }) => order || 0};
`

export const Inner = styled(ContentInner)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-block: var(--spacing-24);
  &[data-open='true'] {
    gap: var(--spacing-24);
    padding-top: var(--spacing-16);
    padding-bottom: var(--spacing-38);
  }
  @media (max-width: 767px) {
    align-items: center;
    gap: var(--spacing-8);
    padding-block: var(--spacing-16);
    &[data-open='true'] {
      gap: var(--spacing-16);
      padding-block: var(--spacing-16);
    }
  }
`

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  width: 100%;
  @media (max-width: 767px) {
    flex-direction: column;
    justify-content: flex-start;
    gap: var(--spacing-8);
  }
`

export const TitleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 20px;
  flex: 1 0 0;
  @media (max-width: 767px) {
    flex-direction: column;
    flex: none;
    width: 100%;
    gap: var(--spacing-8);
  }
`

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
  background: var(--neutral-white);
  @media (max-width: 767px) {
    justify-content: center;
    width: 100%;
  }
`

export const IconBadge = styled.img`
  display: block;
  box-sizing: border-box;
  width: 22px;
  height: 22px;
  padding: 5px 6.5px;
  border-radius: var(--radius-full);
  background: ${GREEN_STATUS};
  flex-shrink: 0;
  object-fit: contain;
`

export const Title = styled.p`
  margin: 0;
  font: 600 12px/20px var(--font-barlow);
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--neutral-darkest);
  white-space: nowrap;
`

export const Subtitle = styled.p`
  margin: 0;
  font: 400 12px/20px var(--font-inter);
  color: var(--neutral-secondary);
  @media (max-width: 767px) {
    text-align: center;
    width: 100%;
  }
`

export const ToggleLabel = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  height: var(--spacing-24);
  align-items: center;
  margin: 0;
  padding: 0;
  color: var(--neutral-secondary);
  font-family: var(--font-inter);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  white-space: nowrap;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: 14%;
  text-underline-position: from-font;
`

export const Toggle = styled.button`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: var(--spacing-16);
  flex: 0 0 auto;
  width: auto;
  max-width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
  line-height: 1;
  appearance: none;
  &:hover ${ToggleLabel} {
    color: var(--neutral-darkest);
  }
`

export const ToggleIcon = styled.span`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: var(--spacing-8);
  background: var(--neutral-white);
  border: var(--border-regular) solid var(--neutral-border-med);
  color: var(--neutral-darkest);
  line-height: 0;
  .anticon {
    display: block;
    font-size: 14px;
    line-height: 1;
    transition: transform 0.25s;
  }
  &[data-open='true'] .anticon {
    transform: rotate(180deg);
  }
`

export const Details = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-24);
  width: 100%;
  @media (min-width: 768px) {
    border-top: var(--border-regular) solid var(--neutral-border-light);
    padding-top: var(--spacing-24);
  }
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: var(--spacing-16);
  }
`

export const Copy = styled.div`
  flex: 1 1 0;
  max-width: 581px;
  font: 400 14px/22px var(--font-inter);
  color: var(--neutral-secondary);
  p {
    margin: 0 0 var(--spacing-16);
    &:last-child {
      margin-bottom: 0;
    }
  }
  b {
    font-weight: 600;
    color: var(--neutral-darkest);
  }
  @media (max-width: 767px) {
    max-width: none;
    font: 400 12px/20px var(--font-inter);
    b {
      font-weight: 400;
    }
  }
`

export const Ladder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 684px;
  flex-shrink: 0;
  @media (max-width: 767px) {
    width: 100%;
  }
  .ladder-title {
    margin: 0;
    font: 600 12px/20px var(--font-barlow);
    letter-spacing: 1px;
    text-transform: uppercase;
    color: var(--neutral-secondary);
  }
`

export const LadderGrid = styled.div`
  display: flex;
  width: 100%;
  @media (max-width: 767px) {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: var(--spacing-8);
  }
`

export const Tier = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 114px;
  height: 90px;
  padding: 32px var(--spacing-40);
  background: var(--neutral-white);
  border: var(--border-regular) solid var(--neutral-border-med);
  @media (max-width: 767px) {
    width: auto;
    height: 80px;
    padding: 20px var(--spacing-8);
  }
  .tierPercent {
    font: 600 20px/26px var(--font-barlow);
    text-transform: uppercase;
    color: var(--neutral-darkest);
  }
  .tierRange {
    font: 600 14px/24px var(--font-barlow);
    color: var(--neutral-secondary);
    white-space: nowrap;
  }
`



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--5!./src/components/DynamicPriceDrop/styledComponents.tsx